import { useTranslation } from 'react-i18next';
import {
  CompaniesWrapper,
  Container,
  CTAButtons,
  InputWrapper,
  NoContentLabel,
  NoContentLabelWrapper,
  SelectWrapper,
  TopWrapper,
} from './ProductsTab.styled';
import Input from 'components/Input/Input';
import { Heart, MagnifyingGlass as Search } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useDebounce } from 'utils/hooks/useDebounce';
import { useGetModels } from './hooks';
import { ITEMS_PER_PAGE } from '../constants';
import { Select } from 'components/Select/Select';
import Button from 'components/Button/Button';
import { COLORS } from 'assets/styled/colors';
import { IModel } from 'types/Model.types';
import ModelCard from 'components/ModelCard/ModelCard';
import { queryClient } from 'index';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import { useSelector } from 'react-redux';
import ProductForm from './ProductForm/ProductForm';
import { RootState } from 'store/store';
import { OrderProductsSteps } from 'types/Order.types';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { useGetCategoryOptions } from 'utils/hooks/useGetCategoryOptions';
import { AppMode } from 'store/Common/reducers/common';
import ModelCardSkeleton from 'components/ModelCard/ModelCardSkeleton/ModelCardSkeleton';
import Pagination from 'components/Pagination/Pagination';

const ProductsTab = () => {
  const { t } = useTranslation();
  const [searchBy, setSearchBy] = useState<string>('');
  const debouncedSearchBy = useDebounce(searchBy);
  const [page, setPage] = useState<number>(1);
  const [isFavorites, setIsFavorites] = useState<boolean>(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [categoriesSearchBy, setCategoriesSearchBy] = useState<string>('');
  const debouncedCategoriesSearchBy = useDebounce(categoriesSearchBy);

  const { companyId, productId, orderProductsStep } = useSelector(
    (state: RootState) => state.orderInfo
  );

  // Clear all filters and search when user is back on selection step
  useEffect(() => {
    setSearchBy('');
    setIsFavorites(false);
    setSelectedCategoryId(null);
    setCategoriesSearchBy('');
    setPage(1);
  }, [orderProductsStep]);

  useEffect(() => {
    if (debouncedSearchBy && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchBy]);

  const { isSmallPhone } = useBreakpointFlag();

  const appMode = useSelector((state: RootState) => state.commonInfo.appMode);

  const { categoryOptions, fetchNextPage, hasNextPage } = useGetCategoryOptions(
    debouncedCategoriesSearchBy,
    appMode === AppMode.BUYER ? companyId : undefined
  );

  const { data: modelsData, isLoading: isLoadingModels } = useGetModels(
    page,
    ITEMS_PER_PAGE,
    debouncedSearchBy,
    selectedCategoryId,
    isFavorites,
    appMode === AppMode.SUPPLIER ? companyId : undefined, // chosen company id in prev step
    appMode === AppMode.BUYER ? companyId : undefined // chosen company id in prev step,
  );

  const renderSkeletons = () => (
    <CompaniesWrapper>
      {Array.from({ length: ITEMS_PER_PAGE }, (_, index) => (
        <ModelCardSkeleton key={index} index={index} opacityCoefficient={0.1} />
      ))}
    </CompaniesWrapper>
  );

  // Product is selected for configuration
  if (orderProductsStep === OrderProductsSteps.Configuration && productId) {
    return <ProductForm />;
  }

  // Selection mode
  return (
    <Container>
      <TopWrapper>
        <InputWrapper>
          <Input
            placeholder={t('Search')}
            width={'100%'}
            onChange={(e: any) => setSearchBy(e.target.value)}
            icon={Search}
            wrapperStyles={{ width: '100%' }}
          />
        </InputWrapper>
        <CTAButtons>
          <Button
            label={t('Favorites')}
            primary={isFavorites}
            secondary={!isFavorites}
            width={isSmallPhone ? '100%' : '150rem'}
            onClick={() => {
              setPage(1);
              setIsFavorites(!isFavorites);
            }}
            icon={
              <Heart
                size={15}
                weight="bold"
                color={!isFavorites ? COLORS.GREEN : COLORS.STRICT_WHITE}
              />
            }
            wrapperStyle={{
              width: isSmallPhone ? '100%' : '150rem',
              padding: isSmallPhone ? '0' : '6rem',
            }}
          />
          <SelectWrapper>
            <Select
              width="100%"
              defaultValue={{ value: '', label: 'All categories' }}
              name="category-select"
              placeholder={t('Category')}
              options={categoryOptions}
              onChange={(selectedOption: any) =>
                setSelectedCategoryId(selectedOption.value)
              }
              onInputChange={(value: string) => setCategoriesSearchBy(value)}
              onMenuScrollToBottom={() => hasNextPage && fetchNextPage()}
            />
          </SelectWrapper>
        </CTAButtons>
      </TopWrapper>

      {isLoadingModels ? (
        renderSkeletons()
      ) : modelsData?.models.length ? (
        <CompaniesWrapper>
          {modelsData.models.map((model: IModel, index: number) => (
            <ModelCard
              model={model}
              key={index}
              onToggleFavoriteSuccess={() => {
                queryClient.invalidateQueries([ReactQueryKeys.GET_MODELS]);
              }}
            />
          ))}
        </CompaniesWrapper>
      ) : (
        <NoContentLabelWrapper>
          <NoContentLabel>
            {t('No products available for this supplier')}
          </NoContentLabel>
        </NoContentLabelWrapper>
      )}

      {modelsData?.pagination?.total > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          perPage={ITEMS_PER_PAGE}
          total={modelsData?.pagination?.total || '0'}
          setPage={setPage}
        />
      )}
    </Container>
  );
};

export default ProductsTab;
